import type { Map as MapboxMap } from 'mapbox-gl';

type Options = {
  /** Target raster size (pixels). e.g., 32 gives a 32x32 canvas */
  size?: number;
  /** Scale factor baked into the bitmap; pair with icon-size later if needed */
  pixelRatio?: number; // default 2
  /** Target logical height (px). Overrides size for height when provided */
  height?: number;
  /** Target logical width (px). Overrides size for width when provided */
  width?: number;
  /**
   * Columns Mapbox may widen when the image is fitted around text (`icon-text-fit`), in the
   * image's own logical units — scaled by `pixelRatio` here, since Mapbox reads them in pixels.
   */
  stretchX?: [number, number][];
  /** Rows it may heighten, same units. */
  stretchY?: [number, number][];
  /** Where the text is laid out: `[left, top, right, bottom]`, same units. */
  content?: [number, number, number, number];
};

/**
 * Rasterizes an SVG string via <canvas> and registers it as a Mapbox image.
 * Works across browsers; avoids createImageBitmap quirks with SVG.
 */
export function addSvgIconViaCanvas(
  map: MapboxMap,
  id: string,
  svg: string,
  opts: Options = {},
): Promise<void> {
  const size = opts.size ?? 32; // logical default size (px) for both axes
  const pixelRatio = opts.pixelRatio ?? 2; // draw larger for crispness
  const targetWidth = opts.width ?? size;
  const targetHeight = opts.height ?? size;

  return new Promise<void>((resolve, reject) => {
    const img = new Image();

    // Important: create a blob URL from the SVG string
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      try {
        // Draw at (target * pixelRatio) for a sharper result
        const w = Math.max(1, Math.round(targetWidth * pixelRatio));
        const h = Math.max(1, Math.round(targetHeight * pixelRatio));

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;

        const ctx = canvas.getContext('2d')!;
        // Draw the SVG scaled to canvas size
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);

        const imageData = ctx.getImageData(0, 0, w, h);

        if (map.hasImage(id)) {
          map.removeImage(id);
        }
        const scale = (value: number) => Math.round(value * pixelRatio);
        map.addImage(id, imageData, {
          pixelRatio,
          ...(opts.stretchX && {
            stretchX: opts.stretchX.map(
              ([from, to]) => [scale(from), scale(to)] as [number, number],
            ),
          }),
          ...(opts.stretchY && {
            stretchY: opts.stretchY.map(
              ([from, to]) => [scale(from), scale(to)] as [number, number],
            ),
          }),
          ...(opts.content && {
            content: opts.content.map(scale) as [number, number, number, number],
          }),
        });

        URL.revokeObjectURL(url);
        resolve();
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };

    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };

    // If your SVG references external assets, uncomment this and ensure CORS:
    // img.crossOrigin = 'anonymous';

    img.src = url;
  });
}
